import { useState } from "react";
import { Mail, Phone, MapPin, CheckCircle2, Send } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { api, formatApiError } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import Seo from "@/components/Seo";

export default function ContactPage() {
  const { t, tl } = useI18n();
  const { profile } = useOutletContext() || {};
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", msg: "" });
    try {
      await api.post("/contact", form);
      setStatus({ type: "success", msg: t("contact.success") });
      setForm({ name: "", email: "", subject: "", phone: "", message: "" });
    } catch (err) {
      setStatus({ type: "error", msg: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title={t("seo.contact.title")} description={t("seo.contact.description")} />
      <section className="relative noise overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-bokeh" style={{ backgroundImage: "url(/visuals/quick-access-bg.png)" }} />
          <div className="absolute -top-20 right-0 w-[40vw] h-[40vw] rounded-full bg-[var(--gold)] opacity-[0.05] blur-[140px]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-12 relative z-10">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-5">
            · {t("contact.heroLabel")}
          </div>
          <h1 className="font-display text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.05] tracking-[-0.035em] max-w-5xl">
            {t("contact.heroTitleA")} <span className="font-serif gold-grad">{t("contact.heroAccent")}</span>{t("contact.heroEnd")}
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 space-y-5">
          <InfoCard icon={Mail} label={t("contact.email")} value={profile?.email || "contact@karimbenna-itconsult.com"} href={`mailto:${profile?.email}`} testid="contact-email" />
          <InfoCard icon={Phone} label={t("contact.phone")} value={profile?.phone || "+49 176 62675596"} href={`tel:${(profile?.phone || "").replace(/\s/g, "")}`} testid="contact-phone" />
          <InfoCard icon={MapPin} label={t("contact.location")} value={tl(profile?.location) || "Deutschland · Portugal"} />
          <div className="svc-card">
            <div className="text-xs uppercase tracking-wider text-[var(--gold)] font-medium mb-3">
              {t("contact.responseTitle")}
            </div>
            <div className="text-[var(--text-0)] text-lg">{t("contact.responseValue")}</div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-8 svc-card p-8 lg:p-12 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("contact.fields.name")} testid="contact-name" value={form.name} onChange={update("name")} required />
            <Field label={t("contact.fields.email")} testid="contact-email-input" type="email" value={form.email} onChange={update("email")} required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("contact.fields.subject")} testid="contact-subject" value={form.subject} onChange={update("subject")} required />
            <Field label={t("contact.fields.phone")} testid="contact-phone-input" value={form.phone} onChange={update("phone")} />
          </div>
          <Field label={t("contact.fields.message")} testid="contact-message" value={form.message} onChange={update("message")} textarea required />

          {status.msg && (
            <div
              data-testid="contact-status"
              role="alert"
              className={`p-4 rounded border ${
                status.type === "success"
                  ? "border-[var(--gold)] bg-[var(--gold)]/5 text-[var(--gold)]"
                  : "border-red-500/40 bg-red-500/5 text-red-400"
              } flex items-center gap-3`}
            >
              <CheckCircle2 size={16} />
              {status.msg}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50"
            >
              <Send size={14} />
              {loading ? t("contact.sending") : t("contact.submit")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, href, testid }) {
  return (
    <div className="svc-card flex items-start gap-4">
      <div className="w-11 h-11 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[var(--gold)]" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-[var(--text-2)] mb-1">{label}</div>
        {href ? (
          <a href={href} data-testid={testid} className="link-gold text-[var(--text-0)] break-words">{value}</a>
        ) : (
          <div className="text-[var(--text-0)]">{value}</div>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", textarea, required }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[var(--text-2)] font-medium">
        {label}
        {required && <span className="text-[var(--gold)] ml-1">*</span>}
      </span>
      {textarea ? (
        <textarea
          rows={6}
          value={value}
          onChange={onChange}
          required={required}
          data-testid={testid}
          className="mt-2 w-full bg-[var(--bg-2)] border hairline rounded focus:border-[var(--gold)] outline-none py-3 px-4 text-[var(--text-0)] resize-y transition-colors"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          data-testid={testid}
          className="mt-2 w-full bg-[var(--bg-2)] border hairline rounded focus:border-[var(--gold)] outline-none py-3 px-4 text-[var(--text-0)] transition-colors"
        />
      )}
    </label>
  );
}
